// Genera la textura de una ficha del tablero con la estética "Nocturno Neón":
// cuerpo azul noche, borde de neón que brilla en el color de su categoría, texto
// claro (tinta) y tipografía geométrica (Space Grotesk). Debe mantenerse coherente
// con las cartas de la mano (.hand-card en global.css).
import * as THREE from 'three';

const cache = new Map();

const TYPE_LABEL = { letter: '', syllable: 'SÍL', prefix: 'PRE', suffix: 'SUF', wildcard: '' };

// Color de acento (neón) por categoría — paleta "Nocturno Neón".
const ACCENT = {
  letter: '#5fe0d0',   // cian
  syllable: '#a3e15c', // lima
  prefix: '#f0785a',   // coral
  suffix: '#b98cf5',   // violeta
  wildcard: '#f2cb4d', // oro
};
const INK = '#eef2fb';           // tinta (texto claro)
const BODY_TOP = '#232c47';      // superficie (arriba)
const BODY_BOT = '#121829';      // noche (abajo)

const FONT = '"Space Grotesk", "Trebuchet MS", system-ui, sans-serif';

// Deben coincidir con GameScene.
const UNIT = 0.95;
const DEPTH = 1.25;
function tileWidth(tile) {
  return UNIT + (Math.max(1, tile.widthUnits || 1) - 1) * 0.72;
}

export function getTileTexture(tile) {
  const text = tile.assignedText ? tile.assignedText : tile.text;
  const acc = ACCENT[tile.type] || ACCENT.letter;
  const key = `${text}|${tile.type}|${tile.widthUnits || 1}`;
  if (cache.has(key)) return cache.get(key);

  const H = 240;
  const W = Math.round(H * (tileWidth(tile) / DEPTH));
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Cuerpo de la ficha, dejando margen para que el resplandor quepa dentro del canvas.
  const margin = 0.11 * H;
  const bx = margin;
  const by = margin;
  const bw = W - margin * 2;
  const bh = H - margin * 2;
  const radius = 0.22 * bh;

  // 1) Resplandor (halo neón alrededor del contorno).
  ctx.save();
  ctx.shadowColor = acc;
  ctx.shadowBlur = 0.17 * H;
  ctx.strokeStyle = acc;
  ctx.lineWidth = 0.03 * H;
  roundRect(ctx, bx, by, bw, bh, radius);
  ctx.stroke();
  ctx.stroke(); // segundo paso: halo más intenso
  ctx.restore();

  // 2) Cuerpo azul noche (tapa el resplandor interior, deja el halo exterior).
  const grad = ctx.createLinearGradient(0, by, 0, by + bh);
  grad.addColorStop(0, BODY_TOP);
  grad.addColorStop(1, BODY_BOT);
  ctx.fillStyle = grad;
  roundRect(ctx, bx, by, bw, bh, radius);
  ctx.fill();

  // Brillo superior sutil (relieve).
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  roundRect(ctx, bx, by, bw, bh * 0.5, radius);
  ctx.fill();

  // 3) Borde neón nítido.
  const lw = 0.024 * H;
  ctx.lineWidth = lw;
  ctx.strokeStyle = acc;
  roundRect(ctx, bx + lw / 2, by + lw / 2, bw - lw, bh - lw, radius);
  ctx.stroke();

  // 4) Etiqueta de categoría (SÍL / PRE / SUF) arriba-izquierda, en el color de acento.
  const label = TYPE_LABEL[tile.type];
  if (label) {
    ctx.save();
    ctx.shadowColor = withAlpha(acc, 0.6);
    ctx.shadowBlur = 0.05 * H;
    ctx.font = `700 ${Math.round(0.115 * H)}px ${FONT}`;
    ctx.fillStyle = acc;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(label, bx + 0.12 * bw, by + 0.09 * bh);
    ctx.restore();
  }

  // 5) Texto principal (centrado). Comodín en oro; el resto en tinta clara.
  const nchars = Array.from(text).length;
  const fontSize = nchars >= 4 ? 0.24 * H : nchars === 3 ? 0.30 * H : 0.46 * H;
  ctx.save();
  ctx.font = `700 ${Math.round(fontSize)}px ${FONT}`;
  const mainCol = tile.type === 'wildcard' ? acc : INK;
  ctx.shadowColor = withAlpha(tile.type === 'wildcard' ? acc : '#0a0e1a', 0.6);
  ctx.shadowBlur = tile.type === 'wildcard' ? 0.09 * H : 0.03 * H;
  ctx.fillStyle = mainCol;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, W / 2, H / 2 + 0.02 * H);
  ctx.restore();

  // 6) Valor / puntos (abajo-derecha) en el color de acento.
  if (tile.type !== 'wildcard') {
    ctx.font = `700 ${Math.round(0.135 * H)}px ${FONT}`;
    ctx.fillStyle = withAlpha(acc, 0.95);
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText(String(tile.value ?? ''), bx + bw - 0.1 * bw, by + bh - 0.07 * bh);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.anisotropy = 4;
  texture.colorSpace = THREE.SRGBColorSpace;
  cache.set(key, texture);
  return texture;
}

export function disposeTileTextures() {
  for (const tex of cache.values()) tex.dispose();
  cache.clear();
}

// --- utilidades de color ---
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const s = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return { r: parseInt(s.slice(0, 2), 16), g: parseInt(s.slice(2, 4), 16), b: parseInt(s.slice(4, 6), 16) };
}
function withAlpha(hex, a) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}
function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
